import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Fade,
  useTheme,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowBack } from '@mui/icons-material';

const StepContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
}));

interface UserInfoStepProps {
  formData: any;
  updateFormData: (field: string, value: string) => void;
  onNext: () => void;
  onBack: () => void;
  appName: string;
}

export default function UserInfoStep({
  formData,
  updateFormData,
  onNext,
  onBack,
  appName
}: UserInfoStepProps) {
  const theme = useTheme();
  const [errors, setErrors] = useState<{ name?: string }>({});

  const validateAndSubmit = () => {
    const newErrors: { name?: string } = {};

    if (!formData.userName || formData.userName.trim().length < 2) {
      newErrors.name = 'Please enter your name (at least 2 characters)';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent any default behavior
      if (formData.userName?.trim().length >= 2) {
        validateAndSubmit();
      }
    }
  };

  return (
    <StepContainer>
      <Fade in={true} timeout={600}>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ mb: 4 }}>
            <IconButton
              onClick={onBack}
              sx={{
                mb: 2,
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              What should we call you? 👋
            </Typography>

            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                mb: 4, 
                maxWidth: 500,
                lineHeight: 1.8
              }}
            >
              Let's personalize your experience. This name will be displayed throughout {appName} and help us communicate with you better.
            </Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <form onSubmit={(e) => { e.preventDefault(); validateAndSubmit(); }}>
              <TextField
                fullWidth
                label="Your Name"
                placeholder="e.g., John Smith"
                value={formData.userName || ''}
                onChange={(e) => updateFormData('userName', e.target.value)}
                onKeyDown={handleKeyPress}
                error={!!errors.name}
                helperText={errors.name}
                autoFocus
                sx={{
                  mb: 3,
                  '& .MuiInputBase-root': {
                    fontSize: '1.1rem',
                  },
                }}
              />
            </form>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
            <Button
              variant="contained"
              size="large"
              onClick={validateAndSubmit}
              disabled={!formData.userName || formData.userName.trim().length < 2}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 12px rgba(255, 255, 255, 0.1)'
                  : '0 4px 12px rgba(0, 0, 0, 0.15)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 6px 20px rgba(255, 255, 255, 0.15)'
                    : '0 6px 20px rgba(0, 0, 0, 0.2)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              Continue
            </Button>

            <Typography 
              variant="caption" 
              color="text.secondary" 
            >
              Press Enter to continue
            </Typography>
          </Box>
        </Box>
      </Fade>
    </StepContainer>
  );
}